import React, {useContext, useCallback, useRef, useState, useEffect} from "react";
import {useReactToPrint} from "react-to-print";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPrint, faTrash} from "@fortawesome/free-solid-svg-icons";

// Utils
import {SettingsContext} from "../../contexts/settings.js";
import {prettyDateInUserTimezone} from "../../utils/helpers.js";
import {DEFAULT_FONT_SIZE} from "../../utils/report.js";
import {useToast} from "../../contexts/toast.js";
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Components
import Modal from "../../components/Modal.js";
import Report from "../report-builder/Report.js";
import RenderReportBuilder from "../report-builder/RenderReportBuilder.js";
import RenderReportBuilderPrintable from "../report-builder/RenderReportBuilderPrintable.js";
import ModalDelete from "../../components/ModalDelete.js";
import FilePreview from "../../components/FilePreview.js";

// Style
import {flex} from "../../style/components/mixins.js";
import {border, pad, radius} from "../../style/components/variables.js";
import {
  ModalButton,
  Inline,
  Heading,
  HeadingMedium,
  Small,
  Text,
  RelativeWrapper,
  AnchorInline,
  NotLoaded,
  Loader
} from "../../style/components/general.js";

const REPORT_WIDTH = 900;

const ModalViewGenerated = ({visible, setVisible, report, record, createdAt, updateData}) => {
  const isMounted = useMountedState();
  const {settings} = useContext(SettingsContext);
  const {addToast} = useToast();

  const {name, base, interval} = report;
  const {header} = record;

  const generatedReport = record.excelTemplate ? null : new Report(record.generated);

  const reportRef = useRef();
  const containerRef = useRef();
  const printRef = useRef();

  const {api} = useApi("report-records");
  const {api: apiFile} = useApi("files");

  const [canPrint, setCanPrint] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [excelFile, setExcelFile] = useState();

  useEffect(() => {
    if (isMounted() && !excelFile && record.excelTemplate)
      apiFile.callGet(record.excelTemplate.id, {download: true}).then(({status, data}) => {
        if (status === 200) setExcelFile(data);
      });
  }, [isMounted, record, apiFile, excelFile]);

  const configurePrint = () => {
    const printBody = document.createElement("div");
    printBody.style.pageBreakInside = "avoid";
    const reportClone = reportRef.current.cloneNode(true);
    printBody.appendChild(reportClone);
    printRef.current = printBody;
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef
  });

  const handleResize = useCallback(() => {
    if (containerRef.current)
      document.documentElement.style.setProperty(
        "--vw-scale",
        Math.max(Math.min(containerRef.current.clientWidth / REPORT_WIDTH, 1), 0.5)
      );
  }, []);

  const containerCallback = useCallback(
    node => {
      if (node !== null) {
        window.addEventListener("resize", handleResize);

        containerRef.current = node;
        document.documentElement.style.setProperty(
          "--vw-scale",
          Math.max(Math.min(node.clientWidth / REPORT_WIDTH, 1), 0.5)
        );
      }
    },
    [handleResize]
  );

  const wrapperCallback = useCallback(node => {
    if (node !== null) {
      document.documentElement.style.setProperty("--target-height", `${node.clientHeight}px`);
    }
  }, []);

  const handleDelete = () => {
    const ids = [record.id];
    api.callDelete(null, {data: {ids}}).then(response => {
      if (response.status === 200) {
        if (updateData) updateData();
        setVisible(false);
      }
    });
  };

  const printError = () => {
    addToast(
      <>
        We are sorry, something went wrong with your report. Please submit a{" "}
        <AnchorInline href="/support" target="_blank" rel="noreferrer">
          Support Ticket
        </AnchorInline>
        .
      </>,
      "error"
    );
  };

  const getExcelContent = () => {
    if (excelFile) return <FilePreview selected={excelFile} />;
    return (
      <NotLoaded>
        <Loader />
      </NotLoaded>
    );
  };

  const controls = (
    <Controls>
      <PrintButton onClick={() => setShowDeleted(true)} title="Delete">
        <FontAwesomeIcon icon={faTrash} />
      </PrintButton>
      <PrintButton
        onClick={
          canPrint
            ? () => {
                configurePrint();
                handlePrint();
              }
            : printError
        }
        title="Print"
        clickableDisabled={!canPrint}>
        <FontAwesomeIcon icon={faPrint} />
      </PrintButton>
    </Controls>
  );

  if (showDeleted)
    return (
      <ModalDelete
        visible={showDeleted && visible}
        setVisible={setVisible}
        hasBackButton
        goBack={() => setShowDeleted(false)}
        confirmDelete={handleDelete}
      />
    );

  return (
    <Modal visible={visible} setVisible={setVisible} renderModalControls={controls}>
      <ModalContent type={record.excelTemplate ? "excel" : "default"}>
        <RelativeWrapper ref={wrapperCallback}>
          <Menu ref={containerCallback}>
            <Heading>{name}</Heading>
            <Small>
              Generated{" "}
              {createdAt
                ? `${prettyDateInUserTimezone(createdAt, settings.timezone, "MMM DD, YYYY")} `
                : ""}
              with data from last{" "}
              {interval === "1" ? base.slice(0, base.length - 1) : `${interval} ${base}`}
            </Small>
          </Menu>

          {record.excelTemplate ? (
            getExcelContent()
          ) : (
            <ReportPreview>
              {header && (
                <HeaderWrapper>
                  <Col align={report.settings?.alignment || "left"}>
                    <Text>{header.topLeft}</Text>
                    <Small>{header.bottomLeft}</Small>
                  </Col>
                  <Col align="center">
                    <HeadingMedium>{header.name}</HeadingMedium>
                    {header.address && (
                      <Text>
                        <span>{header.line1}</span>
                        <br />
                        {header.line2 !== "" && (
                          <>
                            <span>{header.line2}</span>
                            <br />
                          </>
                        )}
                        <span>
                          {header.city} {header.state}
                          {header.zipCode && `, ${header.zipCode}`}
                        </span>
                      </Text>
                    )}
                  </Col>
                  <Col
                    align={
                      !report.settings?.alignment || report.settings?.alignment === "left"
                        ? "end"
                        : report.settings?.alignment
                    }>
                    <Text>{header.topRight}</Text>
                    <Small>{header.bottomRight}</Small>
                  </Col>
                </HeaderWrapper>
              )}
              <RenderReportBuilder
                report={generatedReport}
                showPlaceholders={false}
                fontSize={report.settings?.fontSize || DEFAULT_FONT_SIZE}
                alignment={report.settings?.alignment || "left"}
              />
            </ReportPreview>
          )}
        </RelativeWrapper>

        {/* Printable Report */}
        {!record.excelTemplate && (
          <ReportPreview ref={reportRef} hide>
            {header && (
              <HeaderWrapper>
                <Col align={report.settings?.alignment || "left"}>
                  <Text>{header.topLeft}</Text>
                  <Small>{header.bottomLeft}</Small>
                </Col>
                <Col align="center">
                  <HeadingMedium>{header.name}</HeadingMedium>
                  {header.address && (
                    <Text>
                      <span>{header.line1}</span>
                      <br />
                      {header.line2 !== "" && (
                        <>
                          <span>{header.line2}</span>
                          <br />
                        </>
                      )}
                      <span>
                        {header.city} {header.state}
                        {header.zipCode && `, ${header.zipCode}`}
                      </span>
                    </Text>
                  )}
                </Col>
                <Col
                  align={
                    !report.settings?.alignment || report.settings?.alignment === "left"
                      ? "end"
                      : report.settings?.alignment
                  }>
                  <Text>{header.topRight}</Text>
                  <Small>{header.bottomRight}</Small>
                </Col>
              </HeaderWrapper>
            )}
            <RenderReportBuilderPrintable
              report={generatedReport}
              update={0}
              fontSize={report.settings?.fontSize || DEFAULT_FONT_SIZE}
              alignment={report.settings?.alignment || "left"}
              setCanPrint={setCanPrint}
            />
          </ReportPreview>
        )}
      </ModalContent>
    </Modal>
  );
};

ModalViewGenerated.propTypes = {
  visible: PropTypes.bool,
  setVisible: PropTypes.func.isRequired,
  report: PropTypes.objectOf(PropTypes.any).isRequired,
  record: PropTypes.objectOf(PropTypes.any).isRequired,
  createdAt: PropTypes.string,
  updateData: PropTypes.func
};

ModalViewGenerated.defaultProps = {
  visible: false,
  createdAt: null,
  updateData: null
};

// Style Overrides
const Controls = styled.div`
  display: flex;
  margin-right: ${pad}px;
`;

const ModalContent = styled.div`
  margin-bottom: ${pad}px;
  height: auto;

  ${({type}) =>
    type === "default" &&
    css`
      height: calc(var(--target-height) * var(--vw-scale));
    `}
`;

const Menu = styled(Inline)`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${pad}px;

  @media print {
    margin-bottom: ${pad}px;
    page-break-after: avoid;
    height: 16mm;
  }
`;

const ReportPreview = styled.div`
  border: ${border} solid ${props => props.theme.secondary};
  border-radius: ${radius};
  padding: ${pad}px;
  width: ${REPORT_WIDTH}px;
  min-width: 445px;
  height: min-content;

  /* Print Scale A4 */
  transform: scale(var(--vw-scale));
  transform-origin: top left;

  @media print {
    margin-bottom: 9mm;
    display: block;
    border: 0;
    padding: 0;
  }

  ${props =>
    props.hide
      ? css`
          display: none;
        `
      : ""}
`;

const HeaderWrapper = styled.div`
  ${flex("row", "nowrap")};
  gap: ${pad * 5}px;
  margin: ${pad}px 0 ${pad * 2}px;
  padding: ${pad}px 0;
  border-top: ${border} solid ${props => props.theme.secondary};
  border-bottom: ${border} solid ${props => props.theme.secondary};

  @media print {
    page-break-after: avoid;
  }
`;

const Col = styled.div`
  ${flex("column", "wrap", "space-between", "start")};
  width: 33.33%;

  ${HeadingMedium}, ${Text}, ${Small} {
    color: ${props => props.theme.secondary};
    width: 100%;
    text-align: ${props => props.align || "start"};
  }
`;

const PrintButton = styled(ModalButton)`
  ${props =>
    props.clickableDisabled &&
    css`
      cursor: not-allowed;
    `}
`;

export default ModalViewGenerated;
